import { useState, useEffect } from 'react';
import { Card, Button, LoadingSpinner } from '../components/Layout';
import api from '../services/api';

export function TimeOffPage() {
  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [showRequestForm, setShowRequestForm] = useState(false);

  // Request form state
  const [reqType, setReqType] = useState('vacation');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('8');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balancesData, requestsData] = await Promise.all([
        api.getPTOBalances(),
        api.getTimeOffRequests(),
      ]);
      setBalances(balancesData || []);
      setRequests(requestsData || []);
    } catch (error) {
      console.error('Error loading time off data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Standard day length from the first balance row (backend attaches it).
  const stdHoursPerDay = Number(balances[0]?.hours_per_day) || 8;

  const openForm = () => {
    setReqType('vacation');
    setStartDate('');
    setEndDate('');
    setHoursPerDay(String(stdHoursPerDay));
    setReason('');
    setFormError('');
    setShowRequestForm(true);
  };

  // Live preview of total hours requested = calendar days × hours/day.
  const previewTotalHours = (() => {
    if (!startDate || !endDate || endDate < startDate) return null;
    const days = Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000) + 1;
    const hpd = parseFloat(hoursPerDay);
    if (!Number.isFinite(hpd) || hpd <= 0) return null;
    return Math.round(days * hpd * 100) / 100;
  })();

  const submitRequest = async () => {
    setFormError('');
    if (!startDate || !endDate) { setFormError('Choose a start and end date'); return; }
    if (endDate < startDate) { setFormError('End date must be on or after the start date'); return; }
    const hpd = parseFloat(hoursPerDay);
    if (!Number.isFinite(hpd) || hpd <= 0) { setFormError('Enter hours per day'); return; }
    setSubmitting(true);
    try {
      await api.createTimeOffRequest({
        request_type: reqType,
        start_date: startDate,
        end_date: endDate,
        hours_per_day: hpd,
        reason: reason.trim() || undefined,
      });
      setShowRequestForm(false);
      await loadData();
    } catch (err: any) {
      // Backend throws e.g. "Insufficient vacation balance"
      setFormError(err?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelRequest = async (id: string) => {
    if (!confirm('Cancel this time off request?')) return;
    try {
      await api.cancelTimeOffRequest(id);
      await loadData();
    } catch (err: any) {
      alert(err?.message || 'Failed to cancel request');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-white">Time Off</h1>
          <Button onClick={openForm}>Request Time Off</Button>
        </div>
      </Card>

      {/* PTO Balances — single visible bucket (vacation) for now; Sick/Personal
          are hidden until turned on later. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {balances.filter((b) => b.balance_type === 'vacation').map((balance) => (
          <Card key={balance.balance_type}>
            <div className="text-center">
              <div className="text-gray-400 text-sm">PTO</div>
              <div className="text-3xl font-bold text-yellow-400 mt-2">
                {Number(balance.available_hours ?? 0).toFixed(1)}
              </div>
              <div className="text-gray-500 text-sm mt-1">
                of {Number(balance.total_hours ?? 0).toFixed(1)} hours
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Requests List */}
      <Card>
        <h2 className="text-xl font-bold text-white mb-4">My Requests</h2>
        {requests.length === 0 ? (
          <p className="text-gray-400 text-center py-4">No time off requests</p>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => (
              <div key={request.id} className="bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-white font-medium capitalize">{request.request_type === 'vacation' ? 'PTO' : request.request_type}</div>
                    <div className="text-gray-400 text-sm mt-1">
                      {new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}
                    </div>
                    {request.reason && <div className="text-gray-400 text-sm mt-1">{request.reason}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`px-3 py-1 rounded text-sm font-medium ${
                      request.status === 'approved' ? 'bg-green-700 text-white' :
                      request.status === 'denied' ? 'bg-red-700 text-white' :
                      'bg-yellow-600 text-white'
                    }`}>
                      {request.status}
                    </div>
                    {request.status === 'pending' && (
                      <button
                        onClick={() => cancelRequest(request.id)}
                        className="text-gray-300 hover:text-white text-sm underline"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {showRequestForm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <h2 className="text-xl font-bold text-white mb-4">Request Time Off</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Type</label>
                <select
                  value={reqType}
                  onChange={(e) => setReqType(e.target.value)}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg"
                >
                  <option value="vacation">PTO</option>
                  {/* Sick/Personal hidden until turned on later:
                  <option value="sick">Sick</option>
                  <option value="personal">Personal</option> */}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Start date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">End date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Hours per day</label>
                <input
                  type="number"
                  min={0.25}
                  step={0.25}
                  value={hoursPerDay}
                  onChange={(e) => setHoursPerDay(e.target.value)}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Full day = {stdHoursPerDay}h. Use fewer for a partial day (e.g. 4 for a half day).
                  {previewTotalHours !== null && (
                    <span className="text-yellow-400"> Total: {previewTotalHours} hours.</span>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Reason (optional)</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg"
                />
              </div>
              {formError && <p className="text-red-400 text-sm">{formError}</p>}
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={submitRequest} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Request'}
              </Button>
              <button
                onClick={() => setShowRequestForm(false)}
                className="px-4 py-2 text-gray-300 hover:text-white"
              >
                Cancel
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
